import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, Clock, AlertCircle, Users, FileText } from 'lucide-react';
import { Card } from '@/components/Card';
import { useApi } from '@/hooks/useApi';

interface PolicySummary {
  policy_id: string;
  title: string;
  acknowledged_count: number;
  total_employees: number;
  overdue_count: number;
  pending_count: number;
  status: 'all_signed' | 'partial' | 'none';
}

interface EmployeePolicySummary {
  employee_id: string;
  employee_name: string;
  total_required: number;
  signed_count: number;
  overdue: boolean;
}

interface PoliciesDashboard {
  by_policy: PolicySummary[];
  by_employee: EmployeePolicySummary[];
  pending_count: number;
  overdue_count: number;
}

type View = 'by_policy' | 'by_employee';

export function PoliciesPage() {
  const [view, setView] = useState<View>('by_policy');
  const { data, loading } = useApi<PoliciesDashboard>('legal/policies/dashboard');

  if (loading) {
    return <div className="text-muted-foreground text-sm">Loading policies…</div>;
  }

  const byPolicy = data?.by_policy ?? [];
  const byEmployee = data?.by_employee ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-500" /> Policy Compliance
          </h1>
          {data && (
            <p className="text-sm text-muted-foreground mt-1">
              {data.pending_count} pending · {data.overdue_count} overdue
            </p>
          )}
        </div>

        {/* View toggle */}
        <div className="flex gap-1 bg-surface-2 rounded-lg p-1">
          <button
            onClick={() => setView('by_policy')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              view === 'by_policy' ? 'bg-surface-1 text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <FileText className="w-3.5 h-3.5" /> By Policy
          </button>
          <button
            onClick={() => setView('by_employee')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              view === 'by_employee' ? 'bg-surface-1 text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Users className="w-3.5 h-3.5" /> By Employee
          </button>
        </div>
      </div>

      {/* By Policy View */}
      {view === 'by_policy' && (
        <Card>
          {byPolicy.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No active policies deployed.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Deploy policies from The One Legal to your employee base.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {byPolicy.map(policy => {
                const pct = policy.total_employees > 0
                  ? Math.round((policy.acknowledged_count / policy.total_employees) * 100)
                  : 0;
                return (
                  <div key={policy.policy_id} className="px-4 py-4 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{policy.title}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <div className="flex-1 h-1.5 bg-surface-3 rounded-full overflow-hidden max-w-[180px]">
                          <div
                            className="h-full bg-amber-500 rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {policy.acknowledged_count}/{policy.total_employees}
                        </span>
                        {policy.overdue_count > 0 && (
                          <span className="flex items-center gap-1 text-xs text-red-500">
                            <AlertCircle className="w-3 h-3" /> {policy.overdue_count} overdue
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-xs font-semibold text-foreground">{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* By Employee View */}
      {view === 'by_employee' && (
        <Card>
          {byEmployee.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No employees with pending policies.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {byEmployee.map(emp => (
                <Link
                  key={emp.employee_id}
                  to={`/employees/${emp.employee_id}`}
                  className="flex items-center gap-4 px-4 py-3 hover:bg-surface-2 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 text-xs font-bold shrink-0">
                    {emp.employee_name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{emp.employee_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {emp.signed_count}/{emp.total_required} signed
                      {emp.overdue && <span className="text-red-500 ml-2">· Overdue</span>}
                    </p>
                  </div>
                  <div className="shrink-0">
                    {emp.signed_count === emp.total_required ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : emp.overdue ? (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    ) : (
                      <Clock className="w-4 h-4 text-amber-500" />
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
